import { IPagedResult } from '../paged-result';
import {
  CreateSaleMovementPaymentRequest,
  CreateSaleMovementRequest,
} from './create-sale-movement-request';
import { Payment } from './payment';
import { ISaleMovementDetail } from './sale-movement-detail';
import { ISaleMovement, IPaymentBlock } from './sale-movement';
import { ISaleMovementListItem } from './sale-movement-list-item';

type LegacyPaymentBlock = IPaymentBlock & {
  amountTotalPrepaid?: string;
};

type LegacyPayment = Payment & {
  prepaidInvoice?: Payment['advancePayment'];
};

type LegacySaleMovementLine = ISaleMovementDetail & {
  prepaidDiscountValue?: number;
};

export type LegacySaleMovementResponse = Omit<
  ISaleMovement,
  | 'isAdvancePaymentSale'
  | 'advancePaymentCode'
  | 'hasAdvancePaymentLine'
  | 'hasDeliveryAcceptanceForm'
  | 'canReturn'
  | 'canVoid'
  | 'paymentBlock'
  | 'payments'
  | 'lines'
  | 'installments'
> & {
  installments?: ISaleMovement['installments'];
  canReturn?: boolean;
  canVoid?: boolean;
  isAdvancePaymentSale?: boolean;
  advancePaymentCode?: string;
  hasAdvancePaymentLine?: boolean;
  hasDeliveryAcceptanceForm?: boolean;
  isPrepaidSale?: boolean;
  prepaidCode?: string;
  isPrepaymentSale?: boolean;
  hasDeliveryRecord?: boolean;
  paymentBlock: LegacyPaymentBlock;
  payments?: LegacyPayment[];
  lines?: LegacySaleMovementLine[];
};

export type LegacySaleMovementListItemResponse = ISaleMovementListItem & {
  hasDeliveryRecord?: boolean;
};

export const normalizeSaleMovement = (saleMovement: LegacySaleMovementResponse): ISaleMovement => ({
  ...saleMovement,
  canReturn: saleMovement.canReturn ?? false,
  canVoid: saleMovement.canVoid ?? false,
  isAdvancePaymentSale: saleMovement.isAdvancePaymentSale ?? saleMovement.isPrepaidSale ?? false,
  advancePaymentCode: saleMovement.advancePaymentCode ?? saleMovement.prepaidCode ?? '',
  hasAdvancePaymentLine:
    saleMovement.hasAdvancePaymentLine ?? saleMovement.isPrepaymentSale ?? false,
  hasDeliveryAcceptanceForm:
    saleMovement.hasDeliveryAcceptanceForm ?? saleMovement.hasDeliveryRecord ?? false,
  paymentBlock: saleMovement.paymentBlock
    ? {
        ...saleMovement.paymentBlock,
        amountTotalAdvancePayment:
          saleMovement.paymentBlock.amountTotalAdvancePayment ??
          saleMovement.paymentBlock.amountTotalPrepaid ??
          '',
      }
    : saleMovement.paymentBlock,
  payments: (saleMovement.payments ?? []).map((payment) => ({
    ...payment,
    advancePayment: payment.advancePayment ?? payment.prepaidInvoice,
  })),
  lines: (saleMovement.lines ?? []).map((line) => ({
    ...line,
    advancePaymentDiscountValue: line.advancePaymentDiscountValue ?? line.prepaidDiscountValue ?? 0,
  })),
  installments: saleMovement.installments ?? [],
});

export const normalizeSaleMovementListItem = (
  saleMovement: LegacySaleMovementListItemResponse
): ISaleMovementListItem => ({
  ...saleMovement,
  hasDeliveryAcceptanceForm:
    saleMovement.hasDeliveryAcceptanceForm ?? saleMovement.hasDeliveryRecord ?? false,
});

export const normalizeSaleMovementPage = (
  page: IPagedResult<LegacySaleMovementListItemResponse>
): IPagedResult<ISaleMovementListItem> => ({
  ...page,
  results: (page.results ?? []).map(normalizeSaleMovementListItem),
});

export const toSaleMovementApiRequest = (
  values: CreateSaleMovementRequest
): Omit<CreateSaleMovementRequest, 'isAdvancePaymentSale' | 'payments'> & {
  isPrepaidSale: boolean;
  payments: Array<
    Omit<CreateSaleMovementPaymentRequest, 'advancePayment'> & {
      prepaidInvoice?: CreateSaleMovementPaymentRequest['advancePayment'];
    }
  >;
} => {
  const { isAdvancePaymentSale, payments, ...body } = values;

  return {
    ...body,
    isPrepaidSale: isAdvancePaymentSale,
    payments: payments.map(({ advancePayment, ...payment }) => ({
      ...payment,
      ...(advancePayment ? { prepaidInvoice: advancePayment } : {}),
    })),
  };
};
